import { InitializedConfig } from "../LotteryGameWidgets";
import { AppState } from "./AppState";

/**
 * Interface defining the basic structure of a widget.
 * Each widget must have an `elementId` and a `destroy` method.
 */
export interface WidgetInterface {
    config: InitializedConfig;
    elementId: string;
    destroy(): void;
}

/**
 * Public API for managing widgets. Provides methods for widget lifecycle and interaction.
 */
export interface PublicModelApiInterface{
    /**
     * Event used to handle errors. WidgetSubject enables tracking and reacting to errors during the widget's lifecycle.
     */
    onError: WidgetSubject<WidgetError>;

    /**
     * Destroys the widget and removes its instance from the managed widgets.
     */    
    destroy(): void;

    /**
     * Destroys all managed widgets.
     */
    destroyAll(): void;
}

/**
 * Abstract base class for widgets.
 * Provides common functionality and defines methods that concrete widgets must implement.
 */
export abstract class Widget {
    public config: InitializedConfig;
    /**
    * Constructs a new widget instance with the specified HTML element ID.
    * @param elementId - The ID of the HTML element associated with the widget.
    */
    constructor(public elementId: string, config: InitializedConfig, angularModuleType: any = null) { 
        this.config = config;
    }
    
    /**
    * Abstract method to be implemented by subclasses for initializing the widget.
    */
    protected abstract initialize(): void;

    /**
    * Retrieves the HTML element associated with the widget by its ID.
    * @returns The HTML element or `null` if not found.
    */
    protected getElement(): null | HTMLElement {
        return document.getElementById(this.elementId);
    }

    /**
    * Creates and initializes a new widget instance.
    * @param this - A reference to the widget class.
    * @param args - The arguments required to construct the widget.
    * @returns An initialized instance of the widget.
    */
    public static create<T extends Widget>(this: new (...args: any[]) => T, ...args: ConstructorParameters<typeof Widget>): T {
        const instance = new this(...args);
        instance.initialize();
        return instance;
    }

    public get appState(): AppState {
        return AppState.getInstance(this.config.game.ident, this.config.game.userToken);
    }
}

/**
* Class for managing subscriptions to various resources or events.
*/
export class WidgetSubscription {
    private subscriptions: Array<{ unsubscribe: () => void }> = [];
    /**
     * Adds an unsubscribable object to the list of subscriptions.
     * @param unsubscribable - An object with an `unsubscribe` method.
     */
    add(unsubscribable: { unsubscribe: () => void }): void {
        if (typeof unsubscribable.unsubscribe === 'function') {
            this.subscriptions.push(unsubscribable);
        }
    }
    
    /**
    * Unsubscribes from all subscriptions and clears the list.
    */
    unsubscribe(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }
    
    /**
    * Retrieves the current list of subscriptions.
    * @returns An array of unsubscribable objects.
    */
    getSubscriptions(): Array<{ unsubscribe: () => void }> {
        return this.subscriptions;
    }
}

/**
 * Class for managing event notifications and listeners.
 * @template T - The type of value to notify listeners about.
 */
export class WidgetSubject<T> {
    protected listeners: Array<(value: T) => void> = [];
    protected subscription = new WidgetSubscription();

    /**
     * Notifies all registered listeners with a new value.
     * @param newValue - The value to notify listeners with.
     */
    next(newValue: T): void {
        this.notify(newValue);
    }

    /**
     * Registers a new listener to be notified of events.
     * @param listener - The listener function to register.
     * @returns An object with an `unsubscribe` method to remove the listener.
     */
    subscribe(listener: (value: T) => void): { unsubscribe: () => void } {
        this.listeners.push(listener);
        const subscription = {
            unsubscribe: () => {
                this.listeners = this.listeners.filter(l => l !== listener);
            },
        };
        this.subscription.add(subscription);

        return subscription;
    }

    /**
     * Unsubscribes all listeners.
     */
    unsubscribe(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Notifies all registered listeners with the specified value.
     * @param value - The value to send to listeners.
     */
    protected notify(value: T): void {
        this.listeners.forEach(listener => listener(value));
    }
}

/**
 * Class extending WidgetSubject with the ability to store the latest value.
 * @template T - The type of value managed by the subject.
 */
class WidgetBehaviourSubject<T> extends WidgetSubject<T> {
    private value: T; // Stores the current value

    /**
     * Constructs a new behavior subject with an initial value.
     * @param initialValue - The initial value of the subject.
     */
    constructor(initialValue: T) {
        super();
        this.value = initialValue;
    }

    /**
     * Retrieves the current value of the subject.
     * @returns The current value.
     */
    getValue(): T {
        return this.value;
    }

    /**
     * Updates the current value and notifies all listeners.
     * @param newValue - The new value to set and notify listeners with.
     */
    next(newValue: T): void {
        this.value = newValue;
        super.next(newValue);
    }

    /**
     * Subscribes a listener and immediately notifies it with the current value.
     * @param listener - The listener function to register.
     * @returns An object with an `unsubscribe` method to remove the listener.
     */
    subscribe(listener: (value: T) => void): { unsubscribe: () => void } {
        listener(this.value);
        return super.subscribe(listener);
    }
}

/**
* Interface defining the structure of a widget error.
*/
export interface WidgetError {
    /**
     * Numeric error code representing the type of error.
     */
    code: number;

    /**
     * Description of the error message.
     */
    message: string;
}
